/**
 * /guitar/fret-scale-degrees — Scale Degrees on the Neck.
 *
 * A key-establishing cadence plays, then a single scale degree. The tonic (do)
 * lights on the neck as a dimmed anchor; the user taps where the heard degree
 * sits, relative to it. Single tap auto-submits.
 *
 * The melodic counterpart to the harmonic guitar rungs. Aligned with the
 * ear-training scales drill: pick a scale type (major / minor), how many octaves
 * of the neck to cover, and which degrees to drill (multi-select + presets).
 */

import { useState, useCallback, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { expandNode, MVP_NODES, EXERCISE_SKILL_NODES, type Item } from "@music-app/learning-system";
import {
  generate,
  fretScaleDegreeItemId,
  type FretScaleType,
} from "@music-app/exercise-generator";
import { usePrefsStore } from "@music-app/persistence";
import { ModeSwitcherBar, MODE_COLOR, type ExerciseMode } from "../components/ModeSwitcherBar.js";
import { GuitarDrillCore, GuitarNodeStats, type Round } from "./FretboardExercise.js";
import { nextRoundSeed } from "../lib/round-seed.js";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";

// ─── Item pool (fallback for the shared core; rounds come from roundBuilder) ────

const NODE_IDS = EXERCISE_SKILL_NODES["fret-scale-degrees"];
const ALL_ITEMS: Item[] = NODE_IDS.flatMap((id) =>
  expandNode(MVP_NODES.find((n) => n.id === id)!),
);

const STAT_ROWS = [
  { nodeId: "fret-scale-degrees-major", label: "Major" },
  { nodeId: "fret-scale-degrees-minor", label: "Minor" },
] as const;

// ─── Scale type + degree metadata ───────────────────────────────────────────────

// Major and minor are scheduled curriculum nodes; the five modes are
// free-practice (generic ids). Grouped for the picker.
const SCALE_TYPES: { id: FretScaleType; label: string; scheduled: boolean }[] = [
  { id: "major", label: "Major", scheduled: true },
  { id: "minor", label: "Minor", scheduled: true },
  { id: "dorian", label: "Dorian", scheduled: false },
  { id: "phrygian", label: "Phrygian", scheduled: false },
  { id: "lydian", label: "Lydian", scheduled: false },
  { id: "mixolydian", label: "Mixolydian", scheduled: false },
  { id: "locrian", label: "Locrian", scheduled: false },
];

const SOLFEGE: Record<FretScaleType, readonly string[]> = {
  major:      ["do", "re", "mi", "fa", "sol", "la", "ti"],
  minor:      ["do", "re", "me", "fa", "sol", "le", "te"],
  dorian:     ["do", "re", "me", "fa", "sol", "la", "te"],
  phrygian:   ["do", "ra", "me", "fa", "sol", "le", "te"],
  lydian:     ["do", "re", "mi", "fi", "sol", "la", "ti"],
  mixolydian: ["do", "re", "mi", "fa", "sol", "la", "te"],
  locrian:    ["do", "ra", "me", "fa", "se", "le", "te"],
};

function scaleDegreeNodeId(scaleType: FretScaleType): string {
  if (scaleType === "major") return "fret-scale-degrees-major";
  if (scaleType === "minor") return "fret-scale-degrees-minor";
  return `drill-fret-scale-degrees-${scaleType}`;
}

const isScheduled = (scaleType: FretScaleType): boolean =>
  scaleType === "major" || scaleType === "minor";

const ALL_DEGREES = [1, 2, 3, 4, 5, 6, 7];
const LOWER_DEGREES = [1, 2, 3, 4];
const UPPER_DEGREES = [5, 6, 7];

const OCTAVES = [1, 2, 3];

const SESSION_LENGTHS = [5, 10, 15, 20, 30];
const DEFAULT_SESSION_LENGTH = 10;

const sameSet = (a: number[], b: number[]): boolean =>
  a.length === b.length && a.every((n) => b.includes(n));

// ─── Page ─────────────────────────────────────────────────────────────────────

export function FretScaleDegreeExercisePage(): ReactNode {
  const [searchParams] = useSearchParams();
  const rawMode = searchParams.get("mode") as ExerciseMode | null;
  const mode: Exclude<ExerciseMode, "challenge"> =
    rawMode === "listen" || rawMode === "review" ? rawMode : "practice";
  const from = searchParams.get("from") ?? undefined;
  const coached = searchParams.get("coached") === "true";

  const cadenceTempo = usePrefsStore((s) => s.cadenceTempo);

  const [sessionLength, setSessionLength] = useState(DEFAULT_SESSION_LENGTH);
  const [scaleType, setScaleType] = useState<FretScaleType>("major");
  const [octaves, setOctaves] = useState(1);
  const [degrees, setDegrees] = useState<number[]>(ALL_DEGREES);

  const roundBuilder = useCallback((): Round => {
    const pool = degrees.length > 0 ? degrees : ALL_DEGREES;
    const degree = pool[Math.floor(Math.random() * pool.length)]!;
    const id = fretScaleDegreeItemId(scaleType, degree);
    const nodeId = scaleDegreeNodeId(scaleType);
    const exercise = generate(
      {
        id,
        exerciseType: "fret-scale-degree-recognition",
        params: { degree, mode: scaleType, octaves },
      },
      nextRoundSeed(),
      { cadenceTempo },
    );
    return { exercise, nodeId, tonicMidi: exercise.meta.tonicMidi ?? null };
  }, [scaleType, degrees, octaves, cadenceTempo]);

  return (
    <GuitarDrillCore
      skillId="fret-scale-degrees"
      nodeItems={ALL_ITEMS}
      roundBuilder={roundBuilder}
      exercisePath="/guitar/fret-scale-degrees"
      drillTitle="Scale Degrees"
      mode={mode}
      from={from}
      coached={coached}
      sessionLength={sessionLength}
      renderSetupScreen={(onStart, ready) => (
        <SetupScreen
          mode={mode}
          from={from}
          scaleType={scaleType}
          onScaleType={setScaleType}
          octaves={octaves}
          onOctaves={setOctaves}
          degrees={degrees}
          onDegrees={setDegrees}
          sessionLength={sessionLength}
          onSessionLength={setSessionLength}
          ready={ready}
          onStart={onStart}
        />
      )}
    />
  );
}

// ─── Setup screen ─────────────────────────────────────────────────────────────

function SetupScreen({
  mode,
  from,
  scaleType,
  onScaleType,
  octaves,
  onOctaves,
  degrees,
  onDegrees,
  sessionLength,
  onSessionLength,
  ready,
  onStart,
}: {
  mode: Exclude<ExerciseMode, "challenge">;
  from?: string;
  scaleType: FretScaleType;
  onScaleType: (s: FretScaleType) => void;
  octaves: number;
  onOctaves: (n: number) => void;
  degrees: number[];
  onDegrees: (d: number[]) => void;
  sessionLength: number;
  onSessionLength: (n: number) => void;
  ready: boolean;
  onStart: () => void;
}): ReactNode {
  const isLessonCompleted = usePrefsStore((s) => s.isLessonCompleted);
  const lessonDone = isLessonCompleted("fret-scale-degrees-major");
  const modeColor = MODE_COLOR[mode];

  const ACTIVE = { borderColor: modeColor, backgroundColor: modeColor, color: "#FAF6EE" };
  const INACTIVE = { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE", color: "#1A1813" };
  const [showCustomLength, setShowCustomLength] = useState(false);

  const backLink =
    from === "today" ? { label: "← Today", href: "/" } :
    from === "learn" ? { label: "← Learn", href: "/learn" } :
    { label: "← Guitar Hub", href: "/guitar" };

  const syllables = SOLFEGE[scaleType];

  const toggleDegree = (d: number) => {
    onDegrees(degrees.includes(d) ? degrees.filter((x) => x !== d) : [...degrees, d].sort((a, b) => a - b));
  };
  const preset = (set: number[]) => onDegrees(set);
  const presetActive = (set: number[]) => sameSet(degrees, set);

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={backLink}
        title="Scale Degrees"
        subhead="Hear a degree in key — tap it on the neck, relative to the lit tonic."
      />

      <ModeSwitcherBar current={mode} exercisePath="/guitar/fret-scale-degrees" />

      <GuitarNodeStats rows={STAT_ROWS} />

      {/* Lesson prompt (introductory — not a hard gate) */}
      {!lessonDone && (
        <div className="mb-6 p-4 border border-accent/30 bg-paperWarm">
          <p className="font-serif italic text-sm text-ink mb-3">
            New here? The lesson explains how each scale degree sits relative to
            the tonic on the neck.
          </p>
          <Link
            to="/learn/fret-scale-degrees-major"
            className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent hover:text-ink transition-colors"
          >
            Read lesson →
          </Link>
        </div>
      )}

      {/* Scale type */}
      <section className="mb-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Scale type
        </p>
        <div className="flex flex-wrap gap-2">
          {SCALE_TYPES.map((st) => (
            <button
              key={st.id}
              type="button"
              onClick={() => onScaleType(st.id)}
              aria-pressed={scaleType === st.id}
              className="px-4 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              style={scaleType === st.id ? ACTIVE : INACTIVE}
            >
              {st.label}
            </button>
          ))}
        </div>
        {!isScheduled(scaleType) && (
          <p className="font-mono text-[10px] text-inkFaded mt-2">
            Free practice — modes aren&apos;t scheduled for review.
          </p>
        )}
      </section>

      {/* Octaves */}
      <section className="mb-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Octaves
        </p>
        <div className="flex flex-wrap gap-2">
          {OCTAVES.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onOctaves(n)}
              aria-pressed={octaves === n}
              className="px-4 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              style={octaves === n ? ACTIVE : INACTIVE}
            >
              {n}
            </button>
          ))}
          <span className="self-center font-mono text-[10px] text-inkFaded ml-1">
            {octaves === 1 ? "one box" : `${octaves} octaves up the neck`}
          </span>
        </div>
      </section>

      {/* Degrees — multi-select + presets */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded">
            Degrees
          </p>
          <div className="flex gap-1.5">
            {([
              { label: "All", set: ALL_DEGREES },
              { label: "Lower", set: LOWER_DEGREES },
              { label: "Upper", set: UPPER_DEGREES },
            ] as const).map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => preset(p.set)}
                aria-pressed={presetActive(p.set)}
                className="px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
                style={presetActive(p.set) ? ACTIVE : INACTIVE}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {ALL_DEGREES.map((d) => {
            const active = degrees.includes(d);
            return (
              <button
                key={d}
                type="button"
                onClick={() => toggleDegree(d)}
                aria-pressed={active}
                className="px-3 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
                style={active ? ACTIVE : INACTIVE}
              >
                {syllables[d - 1]}
              </button>
            );
          })}
        </div>
        {degrees.length === 0 && (
          <p className="font-mono text-[10px] text-accent mt-2">Pick at least one degree.</p>
        )}
      </section>

      <section className="mb-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Exercises per session
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          {SESSION_LENGTHS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => { onSessionLength(n); setShowCustomLength(false); }}
              aria-pressed={!showCustomLength && sessionLength === n}
              className="px-4 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              style={
                !showCustomLength && sessionLength === n
                  ? { borderColor: modeColor, backgroundColor: modeColor, color: "#FAF6EE" }
                  : { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE", color: "#1A1813" }
              }
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setShowCustomLength(true)}
            aria-pressed={showCustomLength}
            className="px-4 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
            style={showCustomLength ? { borderColor: modeColor, backgroundColor: modeColor, color: "#FAF6EE" } : { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE", color: "#1A1813" }}
          >
            Other…
          </button>
          {showCustomLength && (
            <input
              type="number"
              min={1}
              max={50}
              value={sessionLength}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v) && v >= 1 && v <= 50) onSessionLength(v);
              }}
              className="w-16 px-3 py-1.5 font-mono text-xs border border-ink/20 bg-paper text-ink text-center focus:outline-none focus:border-ink"
              aria-label="Custom number of exercises"
              autoFocus
            />
          )}
        </div>
      </section>

      <button
        type="button"
        onClick={onStart}
        disabled={!ready || degrees.length === 0}
        aria-busy={!ready}
        className={`mt-8 ${buttonClasses("primary")}`}
        style={{ backgroundColor: MODE_COLOR[mode] }}
      >
        {ready ? "Start →" : "Loading sounds…"}
      </button>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mt-4">
        {ready ? "Audio starts on tap." : "Preparing the instrument…"}
      </p>
    </div>
  );
}
